import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import { 
  TrendingUp, TrendingDown, BarChart3, PieChart, Activity,
  AlertTriangle, Shield, MapPin, Calendar, RefreshCw, Loader2,
  ArrowUp, ArrowDown, Minus, Target, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RiskTrendsPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('90');
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/api/risk-history/stats`, { headers }),
        axios.get(`${API}/api/risk-history/trends?days=${period}`, { headers })
      ]);
      
      setStats(statsRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      console.error('Error fetching risk data:', error);
      toast({ title: 'Error', description: 'Failed to load risk trends', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevelColor = (level) => {
    switch(level) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-slate-400';
    }
  };

  const getRiskLevelBg = (level) => {
    switch(level) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const totalRisks = stats ? Object.values(stats.by_level || {}).reduce((a, b) => a + b, 0) : 0;

  // Calculate trend direction
  const getTrendDirection = () => {
    if (!trends?.weekly_trends || trends.weekly_trends.length < 2) return 'neutral';
    const recent = trends.weekly_trends.slice(-2);
    if (recent.length < 2) return 'neutral';
    const diff = recent[1].avg_score - recent[0].avg_score;
    if (diff > 1) return 'up';
    if (diff < -1) return 'down';
    return 'neutral';
  };

  const trendDirection = getTrendDirection();

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="risk-trends-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Risk Trends</h1>
            <p className="text-slate-500">Track and analyze risk assessments over time</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Assessments</p>
                    <p className="text-3xl font-bold text-slate-900">{trends?.summary?.total_assessments || 0}</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Avg Risk Score</p>
                    <div className="flex items-center gap-2">
                      <p className="text-3xl font-bold text-slate-900">{trends?.summary?.avg_risk_score || 0}</p>
                      {trendDirection === 'down' && <ArrowDown className="w-5 h-5 text-green-500" />}
                      {trendDirection === 'up' && <ArrowUp className="w-5 h-5 text-red-500" />}
                      {trendDirection === 'neutral' && <Minus className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Activity className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Critical Risks</p>
                    <p className="text-3xl font-bold text-red-600">{trends?.summary?.critical_count || 0}</p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Risk Reduction</p>
                    <p className="text-3xl font-bold text-green-600">{trends?.summary?.avg_risk_reduction || 0}%</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <TrendingDown className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weekly Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Risk Score Trend
                </CardTitle>
                <CardDescription>Average risk score by week</CardDescription>
              </CardHeader>
              <CardContent>
                {trends?.weekly_trends?.length > 0 ? (
                  <div className="space-y-4">
                    {/* Simple bar chart */}
                    <div className="flex items-end gap-1 h-40">
                      {trends.weekly_trends.map((week, idx) => {
                        const maxScore = Math.max(...trends.weekly_trends.map(w => w.avg_score || 1), 25);
                        const height = ((week.avg_score || 0) / maxScore) * 100;
                        const isLast = idx === trends.weekly_trends.length - 1;
                        
                        return (
                          <div 
                            key={week.week} 
                            className="flex-1 flex flex-col items-center gap-1"
                            title={`Week ${week.week}: ${week.avg_score} avg score, ${week.total_assessments} assessments`}
                          >
                            <div 
                              className={`w-full rounded-t transition-all ${
                                isLast ? 'bg-purple-500' : 'bg-purple-300'
                              }`}
                              style={{ height: `${Math.max(height, 5)}%` }}
                            />
                            <span className="text-[10px] text-slate-500 truncate w-full text-center">
                              {week.week.split('-W')[1]}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-slate-500 pt-2 border-t">
                      <span>Week numbers shown</span>
                      <span>Current: {trends.weekly_trends[trends.weekly_trends.length - 1]?.avg_score || 0} avg</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                    <BarChart3 className="w-12 h-12 mb-3 opacity-50" />
                    <p>No trend data available</p>
                    <p className="text-sm">Start recording risk assessments to see trends</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Risk Level Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-purple-600" />
                  Risk Level Distribution
                </CardTitle>
                <CardDescription>Active risks by severity level</CardDescription>
              </CardHeader>
              <CardContent>
                {totalRisks > 0 ? (
                  <div className="space-y-4">
                    {/* Visual bars */}
                    <div className="space-y-3">
                      {['critical', 'high', 'medium', 'low'].map(level => {
                        const count = stats?.by_level?.[level] || 0;
                        const percent = totalRisks > 0 ? Math.round((count / totalRisks) * 100) : 0;
                        
                        return (
                          <div key={level} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="capitalize font-medium text-slate-700">{level}</span>
                              <span className="text-slate-500">{count} ({percent}%)</span>
                            </div>
                            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${getRiskLevelColor(level)}`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      {['critical', 'high', 'medium', 'low'].map(level => (
                        <Badge key={level} className={`${getRiskLevelBg(level)} capitalize`}>
                          {level}: {stats?.by_level?.[level] || 0}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                    <PieChart className="w-12 h-12 mb-3 opacity-50" />
                    <p>No risk data available</p>
                    <p className="text-sm">Record risk assessments to see distribution</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Hazard Type */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Risk by Hazard Type
                </CardTitle>
                <CardDescription>Which hazards present the highest risk</CardDescription>
              </CardHeader>
              <CardContent>
                {trends?.by_hazard_type?.length > 0 ? (
                  <div className="space-y-3">
                    {trends.by_hazard_type.slice(0, 6).map((hazard, idx) => (
                      <div key={hazard.hazard_type} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-slate-700 capitalize">
                              {hazard.hazard_type?.replace(/_/g, ' ') || 'General'}
                            </span>
                            <span className="text-sm text-slate-500">
                              {hazard.count} assessment{hazard.count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Progress value={(hazard.avg_score / 25) * 100} className="h-2 flex-1" />
                            <span className="text-xs font-medium text-slate-600 w-10">
                              {hazard.avg_score}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                    <Zap className="w-12 h-12 mb-3 opacity-50" />
                    <p>No hazard data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Highest Risk Areas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-500" />
                  Highest Risk Areas
                </CardTitle>
                <CardDescription>Locations with highest average risk scores</CardDescription>
              </CardHeader>
              <CardContent>
                {stats?.highest_risk_areas?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.highest_risk_areas.map((area, idx) => (
                      <div 
                        key={area.location || idx} 
                        className={`p-3 rounded-lg border ${
                          idx === 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MapPin className={`w-4 h-4 ${idx === 0 ? 'text-red-500' : 'text-slate-400'}`} />
                            <span className="font-medium text-slate-900">
                              {area.location || 'Unknown Location'}
                            </span>
                          </div>
                          <Badge className={idx === 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}>
                            Score: {area.avg_score}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-1 ml-6">
                          {area.count} assessment{area.count !== 1 ? 's' : ''} recorded
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                    <MapPin className="w-12 h-12 mb-3 opacity-50" />
                    <p>No location data available</p>
                    <p className="text-sm">Add locations to risk assessments to track areas</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <CardContent className="py-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Reduce Your Risk Score</h3>
                    <p className="text-sm text-slate-600">Use the Risk Matrix to assess and create CAPAs for hazards</p>
                  </div>
                </div>
                <Button 
                  onClick={() => window.location.href = '/risk-matrix'}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Open Risk Matrix
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RiskTrendsPage;
